import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaTimes } from 'react-icons/fa';
import { toast } from 'react-toastify';
import { useAppDispatch, useAppSelector } from '@/redux/Store';
import { toggleFollow } from '@/redux/thunks/dashboardThunks';
import { LoadingSpinner } from '@/components';
import api from '@/utils/api';
import { getMediaUrl } from '@/constants/media';
import { formatName, nameInitial } from '@/utils/formatName';
import { CreatorRankBadge } from '@/components/CreatorRankBadge';
import './FollowListModal.scss';

interface FollowUser {
  userId: string;
  username: string;
  displayName: string;
  avatar: string | null;
  bio: string | null;
  headline: string | null;
  isFollowing?: boolean;
  stats: {
    followers: number;
    following: number;
  };
}

interface FollowListModalProps {
  isOpen: boolean;
  onClose: () => void;
  username: string;
  type: 'followers' | 'following';
}

const FollowListModal: React.FC<FollowListModalProps> = ({ isOpen, onClose, username, type }) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const currentUserId = useAppSelector((state) => state.auth?.user?.id);
  const [users, setUsers] = useState<FollowUser[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [hasMore, setHasMore] = useState(false);
  const [offset, setOffset] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [followingStates, setFollowingStates] = useState<Record<string, boolean>>({});
  const LIMIT = 20;

  // Fetch users
  const fetchUsers = async (reset = false) => {
    try {
      setIsLoading(true);
      setError(null);
      const currentOffset = reset ? 0 : offset;

      const endpoint = type === 'followers'
        ? `/profile/${username}/followers`
        : `/profile/${username}/following`;

      const response = await api.get(endpoint, {
        params: { limit: LIMIT, offset: currentOffset }
      });

      const data = type === 'followers' ? response.data.followers : response.data.following;

      if (reset) {
        setUsers(data);
        setOffset(LIMIT);
        // Initialize following states
        const states: Record<string, boolean> = {};
        data.forEach((user: FollowUser) => {
          if (user.isFollowing !== undefined) {
            states[user.userId] = user.isFollowing;
          }
        });
        setFollowingStates(states);
      } else {
        setUsers(prev => [...prev, ...data]);
        setOffset(prev => prev + LIMIT);
        // Add new following states
        const newStates = { ...followingStates };
        data.forEach((user: FollowUser) => {
          if (user.isFollowing !== undefined) {
            newStates[user.userId] = user.isFollowing;
          }
        });
        setFollowingStates(newStates);
      }

      setHasMore(response.data.hasMore);
    } catch (err: any) {
      console.error(`Failed to fetch ${type}:`, err);
      setError(`Failed to load ${type}`);
    } finally {
      setIsLoading(false);
    }
  };

  // Load users when modal opens
  useEffect(() => {
    if (isOpen) {
      fetchUsers(true);
    } else {
      // Reset state when modal closes
      setUsers([]);
      setOffset(0);
      setError(null);
      setFollowingStates({});
    }
  }, [isOpen, username, type]);

  // Handle follow/unfollow
  const handleToggleFollow = async (userId: string) => {
    const wasFollowing = !!followingStates[userId];
    const target = users.find((u) => u.userId === userId);
    const displayName = target
      ? (target.displayName || target.username)
      : 'user';
    try {
      await dispatch(toggleFollow({ targetUserId: userId })).unwrap();
      // Update local state
      setFollowingStates(prev => ({
        ...prev,
        [userId]: !prev[userId]
      }));
      toast.success(wasFollowing ? `Unfollowed ${displayName}` : `Following ${displayName}`);
    } catch (error) {
      console.error('Failed to toggle follow:', error);
      toast.error(wasFollowing ? 'Failed to unfollow' : 'Failed to follow');
    }
  };

  // Handle load more
  const handleLoadMore = () => {
    if (!isLoading && hasMore) {
      fetchUsers();
    }
  };

  // Navigate to profile
  const handleProfileClick = (userUsername: string) => {
    navigate(`/profile/@${userUsername}`);
    onClose();
  };

  if (!isOpen) return null;

  const title = type === 'followers' ? 'Followers' : 'Following';

  return (
    <>
      <div className="follow-list-overlay" onClick={onClose} />
      <div className="follow-list-modal">
        {/* Header */}
        <div className="modal-header">
          <h2>{title}</h2>
          <button className="btn-close" onClick={onClose} aria-label="Close">
            <FaTimes />
          </button>
        </div>

        {/* Content */}
        <div className="modal-content">
          {error && (
            <div className="error-message">
              <p>{error}</p>
              <button onClick={() => fetchUsers(true)}>Try Again</button>
            </div>
          )}

          {isLoading && users.length === 0 ? (
            <LoadingSpinner size="small" message={`Loading ${type}...`} />
          ) : users.length === 0 ? (
            <div className="empty-state">
              <p>No {type} yet</p>
            </div>
          ) : (
            <>
              <div className="users-list">
                {users.map((user) => (
                  <div key={user.userId} className="user-item">
                    {/* Avatar */}
                    <div
                      className="user-avatar"
                      onClick={() => handleProfileClick(user.username)}
                    >
                      {user.avatar ? (
                        <img src={getMediaUrl(user.avatar) || undefined} alt={user.username} />
                      ) : (
                        <div className="avatar-placeholder">
                          {nameInitial({ displayName: user.displayName, username: user.username })}
                        </div>
                      )}
                    </div>

                    {/* User info */}
                    <div
                      className="user-info"
                      onClick={() => handleProfileClick(user.username)}
                    >
                      <div className="user-name">
                        {/* formatName already does displayName-then-
                            username, so one line covers both. We don't
                            render @handle separately — the modal is
                            for browsing, not identity lookup. */}
                        <span className="display-name">{formatName({ displayName: user.displayName, username: user.username })}</span>
                        <CreatorRankBadge username={user.username} />
                      </div>
                      {user.headline && <p className="user-headline">{user.headline}</p>}
                      {user.bio && <p className="user-bio">{user.bio}</p>}
                      <div className="user-stats">
                        <span>{user.stats.followers} followers</span>
                        <span className="separator">•</span>
                        <span>{user.stats.following} following</span>
                      </div>
                    </div>

                    {/* Follow button — hidden when the listed user is the current user */}
                    {user.userId !== currentUserId && followingStates[user.userId] !== undefined && (
                      <button
                        className={`btn-follow ${followingStates[user.userId] ? 'following' : ''}`}
                        onClick={() => handleToggleFollow(user.userId)}
                      >
                        {followingStates[user.userId] ? 'Following' : 'Follow'}
                      </button>
                    )}
                  </div>
                ))}
              </div>

              {/* Load more */}
              {hasMore && (
                <div className="load-more-section">
                  <button
                    onClick={handleLoadMore}
                    disabled={isLoading}
                    className="btn-load-more"
                  >
                    {isLoading ? 'Loading...' : 'Load More'}
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </>
  );
};

export default FollowListModal;
